import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaEnvelope, FaPhone, FaUser, FaCalendarAlt, FaCheck, FaTimes, FaEye, FaTrash } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin } from '../utils/normalizeApi';

function ContactManagement() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contact');
      console.log('📩 Fetched contacts:', response.data);
      setContacts(normalizeListForAdmin(response.data));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/contact/${id}`, { status });
      alert(`✅ Status updated to ${status}`);
      fetchContacts();
    } catch (error) {
      alert('❌ Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.delete(`/contact/${id}`);
      alert('✅ Message deleted successfully');
      fetchContacts();
    } catch (error) {
      alert('❌ Error deleting message');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'read': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'replied': return 'bg-green-100 text-green-800 border-green-300';
      case 'archived': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const filteredContacts = filterStatus === 'all' 
    ? contacts 
    : contacts.filter(c => c.status === filterStatus);

  // Stats
  const stats = {
    total: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    read: contacts.filter(c => c.status === 'read').length,
    replied: contacts.filter(c => c.status === 'replied').length
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-blue-900 flex items-center gap-3">
              <FaEnvelope className="text-4xl" />
              Contact Messages
            </h1>
            <p className="text-gray-600 mt-2">Manage all contact form submissions</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Messages</p>
                <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FaEnvelope className="text-2xl text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">New</p>
                <p className="text-3xl font-bold text-green-900">{stats.new}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <span className="text-2xl">✨</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Read</p>
                <p className="text-3xl font-bold text-yellow-900">{stats.read}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <FaEye className="text-2xl text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Replied</p>
                <p className="text-3xl font-bold text-purple-900">{stats.replied}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <FaCheck className="text-2xl text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex gap-3 flex-wrap">
            <button 
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'all' 
                  ? 'bg-blue-900 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({contacts.length})
            </button>
            <button 
              onClick={() => setFilterStatus('new')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'new' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              New ({stats.new})
            </button>
            <button 
              onClick={() => setFilterStatus('read')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'read' 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              Read ({stats.read})
            </button>
            <button 
              onClick={() => setFilterStatus('replied')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterStatus === 'replied' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Replied ({stats.replied})
            </button>
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">From</th>
                  <th className="px-6 py-4 text-left font-semibold">Subject</th>
                  <th className="px-6 py-4 text-left font-semibold">Date</th>
                  <th className="px-6 py-4 text-left font-semibold">Status</th>
                  <th className="px-6 py-4 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredContacts.map((contact) => (
                  <motion.tr 
                    key={contact.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-blue-50 transition"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center gap-2 font-semibold text-gray-800">
                          <FaUser className="text-gray-400" />
                          {contact.name}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                          <span className="flex items-center gap-1">
                            <FaEnvelope className="text-xs" />
                            {contact.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaPhone className="text-xs" />
                            {contact.phone}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className="font-medium text-gray-800 truncate">{contact.subject}</p>
                        <p className="text-sm text-gray-600 truncate mt-1">{contact.message}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FaCalendarAlt className="text-gray-400" />
                        {new Date(contact.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(contact.status || 'new')}`}>
                        {contact.status || 'new'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => updateStatus(contact.id, 'read')}
                          title="Mark as Read"
                          className="p-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition"
                        >
                          <FaEye size={16} />
                        </button>
                        <button 
                          onClick={() => updateStatus(contact.id, 'replied')}
                          title="Mark as Replied"
                          className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                        >
                          <FaCheck size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(contact.id)}
                          title="Delete"
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredContacts.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Messages Found</h3>
            <p className="text-gray-600">
              {filterStatus === 'all' 
                ? 'No contact messages yet' 
                : `No ${filterStatus} messages`}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default ContactManagement;
